// ======================
// CURRENCY FORMATTERS
// ======================

export const formatCurrency = (amount, showRevenue = true) => {
  if (!showRevenue) return '***€';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(parseFloat(amount) || 0);
};

export const formatCurrencyPerHour = (rate, showRevenue = true) => {
  if (!showRevenue) return '***€/h';
  return `${formatCurrency(rate, showRevenue)}/h`;
};

// ======================
// DATE FORMATTERS
// ======================

export const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short' });
};

const LONG_DATE = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

/** "Mardi 6 octobre": French long date, only the first letter capitalized. */
export const formatLongDate = (date) => {
  const text = LONG_DATE.format(new Date(date));
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const SHORT_DATE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

/** "6 oct. 2026". */
export const formatShortDate = (date) => SHORT_DATE.format(new Date(date));

const SHORT_DATE_TIME = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** "6 oct. 2026, 14:05". */
export const formatShortDateTime = (date) => SHORT_DATE_TIME.format(new Date(date));

export const formatYear = (year) => {
  return String(year);
};

export const formatQuarter = (quarter) => {
  return quarter; // Already in format 'Q1', 'Q2', etc.
};

export const formatWeek = (week) => {
  return week; // Already in format 'S1', 'S2', etc.
};

// ======================
// NUMBER FORMATTERS
// ======================

export const formatNumber = (num) => {
  return new Intl.NumberFormat('fr-FR').format(parseInt(num) || 0);
};

export const formatHours = (hours) => {
  return `${parseFloat(hours || 0).toFixed(1)}h`;
};

/** fr-FR percent of a 0–100 value: 66.7 → « 66,7 % ». */
export const formatPercentage = (value, decimals = 1) => new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: decimals, maximumFractionDigits: decimals,
}).format(parseFloat(value || 0)) + '\u00a0%';

// ======================
// GROWTH FORMATTERS
// ======================

export const formatGrowth = (growth) => {
  const sign = growth > 0 ? '+' : '';
  return `${sign}${formatPercentage(growth)}`;
};

// ======================
// GENERIC FORMATTER
// ======================

export const formatValue = (value, format, showRevenue = true) => {
  switch (format) {
    case 'currency':
      return formatCurrency(value, showRevenue);
    case 'currencyPerHour':
      return formatCurrencyPerHour(value, showRevenue);
    case 'hours':
      return formatHours(value);
    case 'number':
      return formatNumber(value);
    case 'percentage':
      return formatPercentage(value);
    case 'growth':
      return formatGrowth(value);
    case 'month':
      return formatMonth(value);
    case 'year':
      return formatYear(value);
    case 'quarter':
      return formatQuarter(value);
    case 'week':
      return formatWeek(value);
    default:
      return value;
  }
};

/** Replaces `{key}` placeholders in a copy string. */
export const fillTemplate = (template, values) =>
  template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));

export const formatPrice = (amount) => {
  const digits = Number.isInteger(amount) ? 0 : 2;
  return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount)}\u00a0€`;
};
